import {orders, ordersPaginated, postReOrder} from '@/services/checkout';
import {
  addSavedCard,
  createClient,
  createProductTeamTemplate,
  deleteSavedCard,
  editSavedCard,
  getCurrentTeamUsers,
  getTeams,
  getUsers,
  removeFromTeam,
  removeTeamProductTemplate,
  removeUser,
  savedCards,
  switchTeam,
  team,
  updateProfile,
  updateTeam,
  updateTeamProductTemplate,
  updateUserProfile,
} from '@/services/dashboard';
import { getItemsPaginated } from "@/services/common";
import {products} from '../services/products';
import {isRounded} from "@/lib/productAttrs";
import {waitFor} from './wait';
import {createToken, loadStripe} from '@/services/stripe';
import splitter from 'full-name-splitter';
import router from '@/router';
import {idFromProductUrl} from '@/lib/selectedProduct';
import {designSetTemplate} from '@/lib/track/events';
import identify from '@/lib/track/identify';
import {clone} from '@/services/design';

function emptyTeam() {
  return {members:[]};
}

function emptyTemplate() {
  return {
    id: null,
    template: null,
    product: null,
    default_qty: null,
    name: null
  };
}

function emptyMember() {
  return {};
}

export default {
  state: {
    teamInfo: {
      team: emptyTeam(),
      template: emptyTemplate(),
      loaded: false,
      busy: false,
      member: emptyMember(),
      members: [],
    },
    recentInfo: {
      orders: null,
      busy: false,
      pageNumber: 1,
      totalOrders: 0,
    },
    productInfo: {
      productRecs: null,
    },
    cardInfo: {
      cards: null,
    },
    allTeamInfo: {
      teams: [],
      busy: false,
      loaded: false,
      pageNumber: 1,
      totalTeams: 0,
      pendingPayload: null,
    },
    usersInfo: {
      users: [],
      busy: false,
      pageNumber: 1,
      totalUsers: 0,
      pendingPayload: null,
    }
  },
  mutations: {
    setRecentOrders(state, orders) {
      state.recentInfo = {orders, loaded: true, busy: false};
    },
    setOrdersPaginated(state, { results, count, pageNumber}) {
      state.recentInfo = { orders: results, totalOrders: count, pageNumber, loaded: true, busy: false };
    },
    startRecentOrders(state) {
      state.recentInfo.busy = true;
    },
    resetDashboardUserState(state) {
      state.recentInfo = {orders: null, busy: false};
      state.teamInfo = {
        team: emptyTeam(), template: emptyTemplate(), loaded: false, busy: false, member: emptyMember(), members: []
      };
    },
    setTeamInfo(state, team) {
      if (team.templates) {
        // Remove this data, so that we can be sure no logic is relying on it.
        // TODO remove from back end
        console.log('team.templates still set!');
        delete team.templates;
      }
      state.teamInfo.team = team;
      state.teamInfo.member = team.member;

      state.teamInfo.busy = false;
      state.teamInfo.loaded = true;
    },
    setCurrentMember(state, id) {
      state.teamInfo.member = state.teamInfo.members.find(m => m.id === id);
    },
    setTeamMembers(state, members) {
      state.teamInfo.members = members;
      state.teamInfo.busy = false;
    },
    determineCurrentProductTemplate(state, user) {
      let product_template;
      let product_templates = state.teamInfo.team ? state.teamInfo.team.product_templates : null;
      if (!product_templates || product_templates.length === 0) {
        state.teamInfo.template = emptyTemplate();
        return;
      }
      let currentId = user && user.current_producttemplate;
      if (currentId && product_templates) {
        // Try to find the template id in the product_templates
        product_template = product_templates.find(p => p.id === currentId);
      }
      // Default to the first product_template
      if (!product_template) product_template = product_templates[0];
      state.teamInfo.template = product_template || emptyTemplate();
    },
    startTeamInfo(state, promise) {
      state.teamInfo.busy = promise;
    },
    setProductRecs(state, productRecs) {
      state.productInfo.productRecs = productRecs;
    },
    updateProductTemplate(state, {id, product_template}) {
      if (id === undefined) id = product_template.id;

      let product_templates = state.teamInfo.team.product_templates;
      let index = product_templates.findIndex(p => p.id === id);
      if (index === -1) throw 'product_template not found';
      product_templates.splice(index, 1, product_template);

      if (state.teamInfo.template && state.teamInfo.template.id === id) {
        state.teamInfo.template = product_template;
      }
    },
    createProductTemplate(state, product_template) {
      // We expect no existing product_templates
      // Caller should update user's current_producttemplate
      state.teamInfo.team.product_templates.push(product_template)
      state.teamInfo.template = product_template;
    },
    setCurrentProductTemplate(state, product_template_id) {
      // Caller should update user's current_producttemplate
      let product_template = state.teamInfo.team.product_templates.find(p => p.id === product_template_id);
      if (!product_template) {
        // Not found -- no effect
        return;
      }
      state.teamInfo.template = product_template;
    },
    setSavedCards(state, cards) {
      state.cardInfo.cards = cards;
    },
    setTeamsLoadingIsBusy(state) {
      state.allTeamInfo.busy = true;
    },
    setTeamsPaginated(state, { results, count, pageNumber }) {
      state.allTeamInfo = { teams: results, totalTeams: count, pageNumber, busy: false, loaded: true };
    },
    setTeamsLoadingPending(state, payload) {
      state.allTeamInfo.pendingPayload = payload;
    },
    updateMember(state, userInfo) {
      // User info got updated. sync with values in the team.users and member.
      let {team, member} = state.teamInfo;
      if (member && member.id === userInfo.id) Object.assign(member, userInfo);
    },
    setUsersLoadingIsBusy(state) {
      state.usersInfo.busy = true;
    },
    setUsersPaginated(state, { results, count, pageNumber }) {
      state.usersInfo = { users: results, totalUsers: count, pageNumber, busy: false };
    },
    setUsersLoadingPending(state, payload) {
      state.usersInfo.pendingPayload  = payload;
    }
  },
  actions: {
    /**
     * Loads recent orders. No action if the orders were recently loaded.
     *
     * @param {*} context
     */
    async loadRecentOrders(context) {
      if (context.state.recentInfo.orders !== null) return;
      context.commit('startRecentOrders');
      let orderList = await orders();
      context.commit('setRecentOrders', orderList);
    },
    /**
     * Load orders for provided page.
     */
    async loadOrdersPaginated({commit}, payload) {
      commit('startRecentOrders');
      let { results, count } = await ordersPaginated(payload);
      const { pageNumber } = payload;
      commit('setOrdersPaginated', { results, count, pageNumber });
    },
    async updateRecentOrders(context) {
      // Something made the recent orders be out of date (eg, order
      // status change, new order, etc). This could reload a minimal set of info.
      // For now just reload the existing orders, if they're stale, but don't load
      // the orders if they weren't loaded yet.
      if (context.state.recentInfo.orders === null) return;
      context.commit('startRecentOrders');
      let orderList = await orders();
      context.commit('setRecentOrders', orderList);
    },
    async reOrder({dispatch}, orderId) {
      await postReOrder(orderId);
      await dispatch('reloadBasket');
      await router.push({ name: 'Cart' });
    },
    async loadTeamInfo({dispatch, state}) {
      if (state.teamInfo.loaded) return;
      if (state.teamInfo.busy) return state.teamInfo.busy;
      dispatch('reloadTeamInfo');
    },
    async reloadTeamInfo(context) {
      let promise = team();
      context.commit('startTeamInfo', promise);
      let teamData = await promise;
      context.commit('setTeamInfo', teamData);
      context.dispatch('setTeamFlags');
      context.commit('determineCurrentProductTemplate', context.getters.user);
    },
    async loadTeamMembers({ dispatch, getters }) {
      if (getters.members && getters.members.length > 0 || getters.isTeamBusy) return;
      return dispatch('reloadTeamMembers');
    },
    async reloadTeamMembers({ commit }) {
      commit('startTeamInfo', true);
      commit('setTeamMembers', await getCurrentTeamUsers());
    },
    async loadTeamMember({dispatch}, id) {
      await dispatch('loadTeamInfo');
      return dispatch('setTeamMember', id);
    },
    async setTeamMember({ dispatch, commit, getters }, id) {
      await dispatch('loadTeamMembers');
      commit('setCurrentMember', id);
      return dispatch('loadMemberAddresses', id);
    },
    async setDesignAsTemplate({getters, dispatch}) {
      // Set the current design as the team template
      let template = getters.designId;
      await dispatch('updateProductTemplate', {template});
      designSetTemplate();
    },
    async updateTeam({getters, commit}, updates) {
      console.log('updateTeam', getters.teamId, updates);
      commit('startTeamInfo');
      // This returns team data but the templates are not serialized in normal way,
      // so make a second call.
      await updateTeam(getters.teamId, updates);
      let teamData = await team();
      commit('setTeamInfo', teamData);
      commit('determineCurrentProductTemplate', getters.user);
      identify(getters.user, getters.team);
    },
    async updateProfile({getters, commit}, updates) {
      console.log('updateProfile', updates);
      let result = await updateProfile(updates);
      console.log('->', result);
      result.id = getters.user.id;
      commit('mergeUser', result);
      commit('updateMember', result);
    },
    async updateUserProfile({commit, dispatch}, {id, ...updates}) {
      console.log('updateUserProfile', id, updates);
      commit('updateMember', {id, ...updates})
      let result = await updateUserProfile(id, updates);
      console.log('->', result);
      return dispatch('reloadTeamInfo');
    },
    /**
    * resetDashboardUserState -- must be called when logged in user changes
    */
    async resetDashboardUserState({commit, getters, dispatch}) {
      commit('resetDashboardUserState');
      if (!getters.isAnonymous) await dispatch('loadTeamInfo');
      commit('updateMember', getters.user);
    },
    async loadProductRecs({state, commit}) {
      if (state.productInfo.productRecs) return;
      commit('setProductRecs', []); // signal loading in process
      let productRecs = await products('any');
      commit('setProductRecs', productRecs);
    },
    async updateProductTemplate({state, getters, commit}, updates) {
      // Updates the current product template. Creates one if necessary.
      let id = getters.productTemplate.id;

      if (updates.product && typeof updates.product === 'object') {
        updates.product = updates.product.id;
      }
      if (updates.template && typeof updates.template === 'object') {
        updates.template = updates.template.id;
      }
      if (id) {
        let product_template = await updateTeamProductTemplate({...updates, id});
        console.log('updates->', product_template);
        commit('updateProductTemplate', {product_template});
      } else {
        // Creates a product template
        let team = getters.teamId;
        let product_template = await createProductTeamTemplate({...updates, team});
        console.log('create->', product_template);
        if (id === 0) {
          commit('updateProductTemplate', {id, product_template});
        } else {
          commit('createProductTemplate', product_template);
        }
        commit('setCurrentProductTemplate', product_template.id);
      }
    },
    async deleteProductTemplates({getters, dispatch}) {
      // For debugging purposes, resets all product templates for team
      let id = getters.productTemplate.id;
      if (id) {
        await removeTeamProductTemplate(id);
      }
      return dispatch('reloadTeamInfo');
    },
    async startOrderFromProductTemplate({getters, dispatch}, orderedFor) {
      let {product, default_qty, template: {id}} = getters.productTemplate
      await dispatch('startDesignFromTemplate', id);
      return dispatch('addToCart', {
        productUrl: product.url,
        boxSize: default_qty,
        designId: getters.designId,
        quantity: 1,
        orderedFor: orderedFor,
      });
    },
    async setCurrentProductTemplate({dispatch, commit}, id) {
      dispatch('updateProfile', {current_producttemplate: id});
      commit('setCurrentProductTemplate', id);
    },
    async addProductTemplate({commit, getters}) {
      let pt = emptyTemplate(), cur = getters.productTemplate;
      pt.default_qty = cur.default_qty;
      pt.product = cur.product.id;
      pt.id = 0;  // mark as unsaved, but in list
      commit('createProductTemplate', pt);
    },
    async cloneProductTemplate({dispatch, commit, getters}) {
      let {id} = getters.productTemplate.template;  // may error
      let cloned = await clone(id, true);
      await dispatch('addProductTemplate');
      await dispatch('updateProductTemplate', {template: cloned.id});
    },
    async createClient(context, clientTeamInfo) {
      await createClient(context.getters.teamId, clientTeamInfo);
      return context.dispatch('reloadTeamInfo');
    },
    async enterTeam({dispatch, commit, getters}, teamId) {
      // Reset stored `TeamAddresses` before switching to another team
      commit('resetTeamAddresses');

      let teamData = await switchTeam(teamId);
      commit('setTeamInfo', teamData);

      // The user info is out of date.
      await dispatch('reloadUser');
      commit('determineCurrentProductTemplate', getters.user);

      // Ensure flags don't carry from one team to other
      return dispatch('resetFlags');
    },
    async loadSavedCards({state, dispatch, commit, getters}) {
      if (state.cardInfo.cards !== null) return;
      await dispatch('waitForTeam');
      let cards = await savedCards(getters.teamId);
      commit('setSavedCards', cards || []);
    },
    async waitForTeam({dispatch, getters}) {
      // We need to be logged in
      await dispatch('waitForLogin');
      // Ensure the team is getting loaded
      dispatch('loadTeamInfo');
      // Then the team needs to be loaded
      await waitFor('hasTeam', 4000);
    },
    async deleteSavedCard({dispatch, commit, getters}, cardId) {
      await deleteSavedCard(getters.teamId, cardId);
      // Cause a reload
      commit('setSavedCards', null);
      return dispatch('loadSavedCards');
    },
    async addSavedCard({dispatch, commit, getters}, info) {
      // createToken expects first and last name to be split.
      // Split if needed.
      let {name, first_name, last_name, title, available_to_team} = info;
      if (name) [first_name, last_name] = splitter(name);
      // createToken expects payment and address as separate objects.
      // Passing the same objects works
      let stripeResult = await createToken(info, {first_name, last_name, ...info});
      if (!stripeResult) throw new Error('Unable to get stripe token');
      await addSavedCard(getters.teamId, stripeResult.id, title, available_to_team);
      // Cause a reload
      commit('setSavedCards', null);
      return dispatch('loadSavedCards');
    },
    async editSavedCard({dispatch, commit, getters}, info) {
      // Edit the title/private attributes from the card
      let {id, title, available_to_team} = info;
      await editSavedCard(getters.teamId, {id, title, available_to_team});
      commit('setSavedCards', null);
      // Cause a reload
      return dispatch('loadSavedCards');
    },
    async loadStripe(context) {
      // TODO track whether loaded?
      return loadStripe();
    },
    async deleteUser({dispatch}, id) {
      await removeUser(id);
      return dispatch('reloadTeamInfo');
    },
    async removeFromTeam({getters, dispatch}, user) {
      await removeFromTeam(getters.team.id, user.id);
      return dispatch('reloadTeamInfo');
    },
    async loadTeamsPaginated({state, commit, dispatch}, payload) {
      // If teams are already loading, save payload to use it later, when current loading will be finished.
      if (state.allTeamInfo.busy) return commit('setTeamsLoadingPending', payload);

      commit('setTeamsLoadingIsBusy');
      const { results, count } = await getItemsPaginated(getTeams, payload);

      const { pageNumber } = payload;
      commit('setTeamsPaginated', { results, count, pageNumber });
      return dispatch('loadTeamsPaginatedPending');
    },
    async loadTeamsPaginatedPending({state, commit, dispatch}) {
      const { pendingPayload } = state.allTeamInfo;
      if (pendingPayload) {
        commit('setTeamsLoadingPending', null);
        return dispatch('loadTeamsPaginated', pendingPayload);
      }
    },
    async loadUsersPaginated({ state, commit, dispatch }, payload) {
      // If users are already loading, save payload to use it later, when current loading will be finished.
      if (state.usersInfo.busy) return commit('setUsersLoadingPending', payload);

      commit('setUsersLoadingIsBusy');
      const { results, count } = await getItemsPaginated(getUsers, payload);

      const { pageNumber } = payload;
      commit('setUsersPaginated', { results, count, pageNumber });
      return dispatch('loadUsersPaginatedPending');
    },
    async loadUsersPaginatedPending({ state, commit, dispatch }) {
      const { pendingPayload } = state.usersInfo;
      if (pendingPayload) {
        commit('setUsersLoadingPending', null);
        return dispatch('loadUsersPaginated', pendingPayload);
      }
    },
    async makeClient({getters}, teamId) {
      // Caller is responsible for refreshing state
      await updateTeam(teamId, {agency: getters.teamId});
    },
    async removeClient({getters}, teamId) {
      // Caller is responsible for refreshing state
      await updateTeam(teamId, {agency: null});
    },
    async setTeamFlags({dispatch, getters}) {
      if (getters.team.flags) {
        dispatch('updateFlags', getters.team.flags);
      }
    },
    async saveTeamFlags({dispatch, getters}) {
      dispatch('updateTeam', {flags: getters.cleanFlags});
    }
  },
  getters: {
    hasTeam: state => state.teamInfo.loaded,
    team: state => state.teamInfo.team || {},
    teamId: state => (state.teamInfo.team || {}).id,
    isTeamBusy: state => state.teamInfo.busy,
    member: state => state.teamInfo.member || {},
    members: state => state.teamInfo.members ? state.teamInfo.members : [],
    recentOrders: state => state.recentInfo.orders,
    recentOrdersBusy: state => state.recentInfo.busy,
    pageNumberOrders: state => state.recentInfo.pageNumber,
    totalOrders: state => state.recentInfo.totalOrders,
    requireProductTemplateChoice: state => {
      let {team} = state.teamInfo;
      if (!team || !team.product_templates || team.product_templates.length < 2) return false;
      return (team.default_orderer_settings || {}).current_producttemplate === '*choose*';
    },
    productTemplates: state => {
      let {product_templates=[]} = state.teamInfo.team || {};
      return product_templates.map((pt, ix) => {
        // Decorate product templates with names if not previously named
        if (!pt.name) pt.name = ix ? `Design ${ix+1}` : 'Main Design';
        return pt;
      });
    },
    hasMultipleProductTemplates: state => {
      let {product_templates=[]} = state.teamInfo.team || {};
      return product_templates.length > 1;
    },
    productTemplate: state => state.teamInfo.template,
    productTemplatePreviewUrls: state => {
      let {template={}} = state.teamInfo.template;
      return template && template.pages && template.pages.map(p => p.preview_data_url);
    },
    productTemplateDesignId: state => {
      let {template} = state.teamInfo.template || {};
      return template && template.id;
    },
    productTemplateIsRounded: state => {
      let t = state.teamInfo.template;
      return t && t.product && isRounded(t.product);
    },
    productTemplateAttrs: (state, getters) => {
      let pt = state.teamInfo.template, pr = state.productInfo.productRecs;
      if (pt && pt.product && pr) {
        return getProductAttrs(pt.product, pr);
      }
      return {};
    },
    productRecs: state => state.productInfo.productRecs,
    impersonating: state => (state.teamInfo.team || {}).impersonating,
    savedCards: state => state.cardInfo.cards || [],
    savedCardsLoaded: state => state.cardInfo.cards !== null,
    defaultSavedCard: state => {
      let {cards} = state.cardInfo;
      console.log('cards', cards);
      // For now return the first one.
      // TODO store last used on users profile
      // TODO is the ordering consistent/sensible?
      // TODO choose "Default" if its there
      if (cards && cards.length > 0) return cards[0].id;
      return null;
    },
    approvalSettings: state => (state.teamInfo.team || {}).approval_settings || {},
    approvalEnabled: (state, getters) => {
      let mode = getters.approvalSettings.approvalMode;
      return mode && mode !== 'none';
    },
    personalInfoSettings: state => (state.teamInfo.team || {}).personal_info_settings || {},
    allTeams: state => state.allTeamInfo.teams,
    allTeamsBusy: state => state.allTeamInfo.busy,
    pageNumberTeams: state => state.allTeamInfo.pageNumber,
    totalTeams: state => state.allTeamInfo.totalTeams,
    users: state => state.usersInfo.users,
    usersBusy: state => state.usersInfo.busy,
    pageNumberUsers: state => state.usersInfo.pageNumber,
    totalUsers: state => state.usersInfo.totalUsers,
    // Whether to force invoicing as checkout method
    // Backend allows invoice, front end doesn't give choice.
    forceInvoice: state => (state.teamInfo.team || {}).allow_invoice,
    userSettings: (state, getters) => (getters.user && getters.user.settings) || {},
  }
}

function getProductAttrs(product, productRecs) {
  for (let {product: vendorProduct} of productRecs) {
    for (let {stockrecord} of vendorProduct.prices) {
      if (+idFromProductUrl(stockrecord.product) === product.id) {
        // We have the correct vendorProduct. extract attrs
        let result = {paper: vendorProduct.name};
        for (let {sku_tag} of vendorProduct.attributes) {
          let [key, value] = sku_tag.split(':');
          result[key] = value;
        }
        return result;
      }
    }
  }
  return {};
}



// WEBPACK FOOTER //
// ./src/store/dashboard.js